import React, { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { localizePath, stripLocale } from "@/lib/localePath";
import { AuroraWordmark } from "./AuroraHeart";
import { StartFreeButton } from "./StoreBadges";
import { HOME } from "@/constants/testIds";
import { track } from "@/lib/analytics";
import { WhatsNewBadge } from "./WhatsNewBadge";
import { Menu, X, Globe } from "lucide-react";

const NAV_LINKS = [
  { to: "/features", labelKey: "nav.features", fallback: "Features" },
  { to: "/faith", labelKey: "nav.faith", fallback: "Faith" },
  { to: "/knowledge", labelKey: "nav.knowledge", fallback: "Knowledge" },
  { to: "/best-ai-couples-apps-2026", labelKey: "nav.explore", fallback: "Explore" },
  { to: "/faq", labelKey: "nav.faq", fallback: "FAQ" },
];

const LANGS = [
  { code: "en", label: "EN", flag: "🇺🇸" },
  { code: "es", label: "ES", flag: "🇪🇸" },
  { code: "pt", label: "PT", flag: "🇧🇷" },
  { code: "de", label: "DE", flag: "🇩🇪" },
  { code: "zh", label: "ZH", flag: "🇨🇳" },
];

export const Nav = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const L = (to) => localizePath(to, i18n.language);

  const closeMenus = useCallback(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, []);

  const onScroll = useCallback(() => {
    setScrolled(window.scrollY > 30);
  }, []);

  useEffect(() => {
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useEffect(() => {
    closeMenus();
  }, [location.pathname, closeMenus]);

  const changeLang = (code) => {
    const previous = i18n.language;
    const { path } = stripLocale(location.pathname);
    i18n.changeLanguage(code);
    setLangOpen(false);
    navigate(`${localizePath(path, code)}${location.hash || ""}`);
    track("language_change", { from: previous, to: code });
  };

  const currentLang = LANGS.find((l) => l.code === i18n.language?.slice(0, 2)) || LANGS[0];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#12052E]/85 border-b border-white/5"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      {/* Announcement bar */}
      <div className="hidden md:flex items-center justify-center gap-2 text-[0.78rem] py-2 px-4 border-b border-white/5 bg-[#12052E]/40">
        <span className="text-[var(--aurora-gold)]">●</span>
        <span className="text-white/75 font-light">
          {t("announcement.message", "The first AI system that converts relationship behavior into personalized guidance aligned with your beliefs.")}
        </span>
        <Link
          to={L("/")}
          className="text-[var(--aurora-gold)] font-semibold underline-offset-4 hover:underline"
        >
          {t("announcement.cta", "Start now")} →
        </Link>
      </div>

      <div className="mx-auto max-w-7xl px-5 lg:px-8 flex items-center justify-between h-16">
        <Link to={L("/")} data-testid={HOME.navLogo} className="shrink-0">
          <AuroraWordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-[0.92rem] font-sans">
          {NAV_LINKS.map((l) => {
            const isHash = l.to.includes("#");
            if (isHash) {
              return (
                <a
                  key={l.to}
                  href={L(l.to)}
                  className="text-white/70 hover:text-white transition"
                  data-testid={`nav-${l.labelKey}`}
                >
                  {t(l.labelKey, l.fallback)}
                </a>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={L(l.to)}
                data-testid={`nav-${l.labelKey}`}
                className={({ isActive }) =>
                  `transition ${
                    isActive ? "text-white" : "text-white/65 hover:text-white"
                  }`
                }
              >
                {t(l.labelKey, l.fallback)}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <WhatsNewBadge />
          <div className="relative hidden md:block">
            <button
              onClick={() => setLangOpen((v) => !v)}
              data-testid={HOME.langSwitcher}
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-sm text-white/75 hover:text-white hover:border-white/30"
              aria-label="Change language"
            >
              <Globe size={14} />
              <span className="font-medium">{currentLang.label}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-36 rounded-2xl bg-[#1B0A3F]/95 backdrop-blur-2xl border border-white/10 shadow-2xl py-2 z-50">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => changeLang(l.code)}
                    data-testid={`lang-option-${l.code}`}
                    className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/5 ${
                      l.code === currentLang.code
                        ? "text-[var(--aurora-gold)]"
                        : "text-white/80"
                    }`}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <StartFreeButton
            testId={HOME.navStartFree}
            label={t("nav.create", "Start Free Now")}
            className="hidden sm:inline-flex !py-2.5 !px-3 md:!px-4 !text-[0.85rem] md:!text-[0.92rem]"
          />

          <button
            data-testid={HOME.mobileMenuTrigger}
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 text-white"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-[#0B0322]/95 backdrop-blur-2xl"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between px-5 h-16">
            <AuroraWordmark />
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 text-white"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={L(l.to)}
                className="font-serif text-3xl text-white/90"
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${l.labelKey}`}
              >
                {t(l.labelKey, l.fallback)}
              </Link>
            ))}
            <div className="pt-6 mt-4 border-t border-white/10">
              <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">
                Language
              </p>
              <div className="flex flex-wrap gap-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => changeLang(l.code)}
                    className={`rounded-full px-3 py-1.5 text-sm border ${
                      l.code === currentLang.code
                        ? "bg-[var(--aurora-gold)] text-[#12052E] border-transparent"
                        : "border-white/15 text-white/70"
                    }`}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-6">
              <WhatsNewBadge variant="mobile" />
            </div>
            <div className="pt-4">
              <StartFreeButton label={t("nav.create", "Start Free Now")} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
